import React, {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { useViewportCssFullScreen } from '@/pages/dashboard/hooks/useViewportCssFullScreen';
import { useFullscreenViewportHeight } from '@/pages/dashboard/hooks/useFullscreenViewportHeight';
import type { IViewportFullscreenApi } from './types';
import './index.scss';

const noop = (): void => {};

const noopSetChildSortSnapshot = (_id: string, _value: unknown): void => {};

/** pageConfig 常把「全屏层」与「--active」写在同一 layerClassName；非全屏时不能套用 --active，否则会 fixed 盖住整页 */
function layerClassNameForInlineMode(layerClassName: string): string {
  return layerClassName
    .split(/\s+/)
    .filter(Boolean)
    .filter(token => !token.endsWith('--active'))
    .join(' ')
    .trim();
}

function resolveInitialViewportHeight(): number {
  return typeof window !== 'undefined' ? window.innerHeight : 800;
}

function createDisabledViewportFullscreenApi(): IViewportFullscreenApi {
  return {
    enabled: false,
    isFullScreen: false,
    requestFullScreen: noop,
    exitFullScreen: noop,
    toggleFullScreen: noop,
    viewportHeight: resolveInitialViewportHeight(),
    getChildSortSnapshot: () => undefined,
    setChildSortSnapshot: noopSetChildSortSnapshot,
  };
}

export interface IViewportFullscreenShellProps {
  /** 为 false 时不走 Portal，并向子节点注入禁用态的 viewportFullscreen（props / 函数参数） */
  enabled: boolean;
  /** pageConfig 常用：写在 Shell 外层，等价于包裹一层容器 */
  className?: string;
  style?: React.CSSProperties;
  /** Portal 根节点 className，通常含布局修饰符（如 `xxx-fs-root--active`） */
  layerClassName: string;
  backdropClassName?: string;
  placeholderClassName?: string;
  /** 占位高度，避免全屏时原布局塌陷 */
  placeholderMinHeight?: number | string;
  /** 非全屏时包裹内容的容器 className */
  inlineHostClassName?: string;
  /**
   * 全屏时内容区的字号放大倍数，默认 1（不放大）。
   * 原理：在全屏层根节点注入 `fontSize: \`${scale * 100}%\``，内部使用 em 的样式自动等比放大。
   */
  fullscreenScale?: number;
  /**
   * 全屏时 X 轴（ECharts xAxis）字号放大倍数；未设则与 fullscreenScale 一致。
   */
  fullscreenXAxisScale?: number;
  /**
   * - 子组件为 ReactElement，或由 Render.map 产生的 **单元素数组** `[<Render/>]`：统一注入 `viewportFullscreen`
   * - 函数式：`(api) => ReactNode`，便于多节点布局（配置内外层包裹时常用）
   */
  children: React.ReactNode | ((api: IViewportFullscreenApi) => React.ReactNode);
}

/**
 * 视口 CSS 全屏外壳：单一 Portal 挂载点在内联槽与 body 之间切换，子树不重挂、状态不丢。
 * 不使用 Context：子组件通过 `viewportFullscreen` prop 或函数式 children 接收 api。
 */
const ViewportFullscreenShell: React.FC<IViewportFullscreenShellProps> = ({
  enabled,
  className,
  style,
  layerClassName,
  backdropClassName = 'dbd-viewport-fs-backdrop',
  placeholderClassName = 'dbd-viewport-fs-placeholder',
  placeholderMinHeight = 420,
  inlineHostClassName,
  fullscreenScale,
  fullscreenXAxisScale,
  children,
}) => {
  const {
    isFullScreen,
    requestFullScreen,
    exitFullScreen,
    toggleFullScreen,
    inlineRootRef,
    fullScreenLayerRef,
  } = useViewportCssFullScreen();

  const viewportHeight = useFullscreenViewportHeight(enabled, isFullScreen);

  const disabledApi = useMemo(createDisabledViewportFullscreenApi, []);

  const childSortSnapshotsRef = useRef<Record<string, unknown>>({});
  const getChildSortSnapshot = useCallback((id: string) => {
    return childSortSnapshotsRef.current[id];
  }, []);
  const setChildSortSnapshot = useCallback((id: string, value: unknown) => {
    if (value === undefined) {
      delete childSortSnapshotsRef.current[id];
    } else {
      childSortSnapshotsRef.current[id] = value;
    }
  }, []);

  const api = useMemo(
    (): IViewportFullscreenApi => ({
      enabled,
      isFullScreen,
      requestFullScreen,
      exitFullScreen,
      toggleFullScreen,
      viewportHeight,
      scale: isFullScreen ? fullscreenScale : undefined,
      /** 仅显式配置 fullscreenXAxisScale 时注入；未配置由子组件回退 fsScale */
      xAxisScale:
        isFullScreen && fullscreenXAxisScale !== undefined
          ? fullscreenXAxisScale
          : undefined,
      getChildSortSnapshot,
      setChildSortSnapshot,
    }),
    [
      enabled,
      isFullScreen,
      requestFullScreen,
      exitFullScreen,
      toggleFullScreen,
      viewportHeight,
      fullscreenScale,
      fullscreenXAxisScale,
      getChildSortSnapshot,
      setChildSortSnapshot,
    ],
  );

  const renderChild = useCallback(
    (inject: IViewportFullscreenApi): React.ReactNode => {
      if (typeof children === 'function') {
        return (children as (a: IViewportFullscreenApi) => React.ReactNode)(
          inject,
        );
      }
      const childList = Children.toArray(children);
      if (childList.length > 0) {
        const injectedProps: {
          viewportFullscreen: IViewportFullscreenApi;
        } = {
          viewportFullscreen: inject,
        };
        const clonedList = childList.map(node =>
          isValidElement(node)
            ? cloneElement(node, injectedProps)
            : node,
        );
        if (clonedList.length === 1) {
          return clonedList[0];
        }
        return <>{clonedList}</>;
      }
      return children;
    },
    [children],
  );

  /** 非全屏时 Portal 挂在此节点内，全屏时挂到 body；切换容器时 React 移动同一子树，内部 state 保留 */
  const [inlinePortalMount, setInlinePortalMount] = useState<HTMLDivElement | null>(
    null,
  );

  const bindInlinePortalMount = useCallback(
    (node: HTMLDivElement | null) => {
      setInlinePortalMount(node);
    },
    [],
  );

  const bindOuterHost = useCallback(
    (node: HTMLDivElement | null) => {
      (inlineRootRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [inlineRootRef],
  );

  if (!enabled) {
    return (
      <div className={className} style={style}>
        {renderChild(disabledApi)}
      </div>
    );
  }

  const placeholderStyle: React.CSSProperties = {
    ...(placeholderMinHeight !== undefined
      ? { minHeight: placeholderMinHeight }
      : {}),
    ...style,
  };

  let portalContainer: HTMLElement | null = null;
  if (typeof document !== 'undefined') {
    portalContainer = isFullScreen ? document.body : inlinePortalMount;
  }

  useEffect(() => {
    const overlayEl = document.querySelector('#tea-overlay-root .db-bubble-close-overlay') as HTMLElement | null;
    const originalZIndex = overlayEl?.style.zIndex;
    if (overlayEl) {
      overlayEl.style.zIndex = '1000';
    }
    return () => {
      if (overlayEl) {
        overlayEl.style.zIndex = originalZIndex ?? '';
      }
    };
  }, []);

  const fullscreenLayerStyle: React.CSSProperties | undefined =
    isFullScreen && fullscreenScale !== undefined && fullscreenScale !== 1
      ? {
        fontSize: `${fullscreenScale * 100}%`,
        '--dbd-fullscreen-scale': fullscreenScale,
      } as React.CSSProperties
      : undefined;

  const portalContent = (
    <div
      ref={fullScreenLayerRef}
      className={classNames(
        isFullScreen ? layerClassName : layerClassNameForInlineMode(layerClassName),
        !isFullScreen && 'dbd-viewport-fs-layer--inline',
      )}
      style={fullscreenLayerStyle}
    >
      {isFullScreen && <div className={backdropClassName} aria-hidden />}
      {renderChild(api)}
    </div>
  );

  return (
    <>
      <div
        ref={bindOuterHost}
        className={classNames(
          'dbd-viewport-fs-inline-host',
          inlineHostClassName,
          isFullScreen && placeholderClassName,
          className,
        )}
        style={isFullScreen ? placeholderStyle : style}
        aria-hidden={isFullScreen ? true : undefined}
      >
        <div
          ref={bindInlinePortalMount}
          className="dbd-viewport-fs-inline-mount"
        />
      </div>
      {portalContainer
        ? (createPortal(portalContent, portalContainer) as unknown as React.ReactNode)
        : null}
    </>
  );
};

export default ViewportFullscreenShell;
